import { useQuery } from "@tanstack/react-query";

import { api, type CallTotals, type ReportScope } from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { formatInZone } from "../../../lib/time";
import { Delta, rangeIsReady, type RangeValue } from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TIME_SERIES_CHARTS } from "../card/chart-types";

/**
 * The phone, as the desk used it: calls taken, missed and made.
 *
 * The figures come from the phone system's own call history, synced into
 * Pulse, rather than from calls somebody logged -- so they are every call, not
 * the ones people remembered to write up.
 *
 * **Missed means something different on a narrower view, and the card says
 * so.** On the whole desk it is every inbound call nobody answered. On a team
 * or one agent it is only calls dialled straight to those people, because a
 * call to the support line that nobody picked up was not any one person's to
 * miss -- and counting it against a team would charge them for a line they
 * were never the only ones on. The label changes rather than the number
 * quietly meaning less.
 */

const SERIES = [
  { key: "taken", label: "Taken" },
  { key: "missed", label: "Missed" },
  { key: "made", label: "Made" },
] as const;

export function CallsReport({
  layout,
  scope,
  isManager,
}: {
  layout: DashboardLayout;
  isManager: boolean;
  scope: ReportScope;
}) {
  const stored = layout.card("calls");
  const scoped = withCardFilters(scope, stored);

  return (
    <ReportCard
      id="calls"
      title="Phone calls"
      subtitle="Calls taken, missed and made, from the phone system's own history"
      layout={layout}
      offerAgents={isManager}
      charts={TIME_SERIES_CHARTS}
      series={SERIES}
      footnote={
        <p>
          Talk time and the median leave out ringing, unanswered calls and
          calls between colleagues. A call is attributed to an agent by their
          phone user, linked in Settings → Users; calls by a phone user nobody
          has been linked to count towards the desk but nobody in particular.
        </p>
      }
    >
      {({ chart, colorFor, range }) => (
        <Body scope={scoped} range={range} chart={chart} colorFor={colorFor} />
      )}
    </ReportCard>
  );
}

function Body({
  scope,
  range,
  chart,
  colorFor,
}: {
  scope: ReportScope;
  range: RangeValue;
  chart: Parameters<typeof CardChart>[0]["chart"];
  colorFor: (key: string) => string;
}) {
  const ready = rangeIsReady(range);

  const report = useQuery({
    queryKey: queryKeys.dashboardReport("calls", range, scope),
    queryFn: () => api.callsReport(range, scope),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  if (!ready) {
    return <p className="py-6 text-center text-xs text-faint">Pick both dates.</p>;
  }

  const data = report.data;

  if (data && !data.enabled) {
    return (
      <p className="py-6 text-center text-xs text-faint">
        The phone system is not connected on this server, so there are no call
        figures — not that there were no calls.
      </p>
    );
  }

  const totals = data?.enabled ? data.totals : null;
  const previous = data?.enabled ? data.previous : null;
  const granularity = data?.enabled ? data.granularity : "daily";
  const lineCalls = data?.enabled ? data.includesLineCalls : true;

  const rows = (data?.enabled ? data.points : []).map((point) => ({
    label: bucketLabel(point.from, granularity),
    values: { taken: point.taken, missed: point.missed, made: point.made },
  }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Figure
          label="Taken"
          value={totals ? totals.inboundAnswered.toLocaleString() : "—"}
          change={change(totals, previous, (t) => t.inboundAnswered)}
          note={totals ? `of ${totals.inbound.toLocaleString()} inbound` : " "}
        />
        <Figure
          label={lineCalls ? "Missed" : "Missed, dialled direct"}
          value={totals ? totals.missed.toLocaleString() : "—"}
          change={change(totals, previous, (t) => t.missed)}
          higherIsBetter={false}
          note={
            totals?.missedRate == null
              ? " "
              : `${Math.round(totals.missedRate * 100)}% of inbound`
          }
        />
        <Figure
          label="Made"
          value={totals ? totals.outbound.toLocaleString() : "—"}
          change={change(totals, previous, (t) => t.outbound)}
          note={totals ? `${totals.outboundAnswered.toLocaleString()} connected` : " "}
        />
        <Figure
          label="Median call"
          value={formatSeconds(totals?.medianSeconds ?? null)}
          /* Neither direction is good news: a shorter call can be a brisker
             one or a fobbed-off customer. Shown without colour. */
          change={null}
          note="answered calls"
        />
        <Figure
          label="Talk time"
          value={formatTalk(totals?.talkSeconds ?? null)}
          change={change(totals, previous, (t) => t.talkSeconds)}
          note="with customers"
        />
      </div>

      {!lineCalls ? (
        <p className="rounded-md bg-surface-2 px-2 py-1.5 text-[11px] text-muted-on-tint">
          Missed calls to a shared line belong to nobody in particular, so this
          view counts only calls rung straight to these people. The whole desk
          view has the rest.
        </p>
      ) : null}

      {data?.enabled && data.capped ? (
        <p className="text-[11px] text-faint">
          This period holds more calls than one report reads, so the figures
          cover only part of it. Narrow the range for exact numbers.
        </p>
      ) : null}

      <CardChart
        chart={chart}
        rows={rows}
        series={[...SERIES]}
        colorFor={colorFor}
        format={(value) => (value === null ? "—" : value.toLocaleString())}
        emptyMessage={report.isLoading ? "Loading…" : "No calls in this period."}
      />
    </div>
  );
}

function Figure({
  label,
  value,
  change,
  note,
  higherIsBetter = true,
}: {
  label: string;
  value: string;
  change: number | null;
  note: string;
  higherIsBetter?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-faint">{label}</p>
      <p className="flex items-baseline gap-2">
        <span className="text-xl font-semibold tabular-nums text-content">{value}</span>
        <Delta points={change} higherIsBetter={higherIsBetter} suffix="%" />
      </p>
      <p className="text-[11px] text-faint">{note}</p>
    </div>
  );
}

/**
 * Per cent change against the same span of the previous period.
 *
 * Null rather than infinity when the earlier period had none: "up from
 * nothing" is not a percentage, and an arrow claiming +∞% is noise.
 */
function change(
  current: CallTotals | null,
  previous: CallTotals | null,
  pick: (totals: CallTotals) => number,
): number | null {
  if (!current || !previous) return null;
  const before = pick(previous);
  if (before === 0) return null;
  return ((pick(current) - before) / before) * 100;
}

/** "4m 05s", or "45s" under a minute. */
export function formatSeconds(seconds: number | null): string {
  if (seconds === null) return "—";
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes === 0 ? `${rest}s` : `${minutes}m ${String(rest).padStart(2, "0")}s`;
}

/** Hours and minutes: a day of talk time in seconds is not a readable number. */
function formatTalk(seconds: number | null): string {
  if (seconds === null) return "—";
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  return hours === 0 ? `${minutes}m` : `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

function bucketLabel(iso: string, granularity: string): string {
  const options: Intl.DateTimeFormatOptions =
    granularity === "hourly"
      ? { hour: "2-digit", minute: "2-digit" }
      : granularity === "monthly"
        ? { month: "short", year: "2-digit" }
        : { day: "numeric", month: "short" };

  return formatInZone(iso, options);
}
